import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useModalTransition from '../../hooks/useModalTransition';
import { checkBetaTester, submitWaitlist } from '../../utils/api';
import Button from '../shared/Button';
import Icon from '../shared/Icon';
import './AuthModal.css';

const PUBLIC_MODES = ['betaTester', 'waitlist'];

const INTEREST_OPTIONS = [
  { value: 'general_waitlist', label: 'General Waitlist' },
  { value: 'consumer',         label: 'AI Personal Trainer' },
  { value: 'coach',            label: 'Coaching Platform' },
  { value: 'facility',         label: 'Physical Locations' },
  { value: 'investor',         label: 'Investor Updates' },
];

function toInquiryType(interest) {
  if (interest === 'investor') return 'investor';
  if (interest === 'coach')    return 'coach';
  return 'general';
}

const WAITLIST_HEADLINES = {
  investor:         'Thanks for reaching out.',
  coach:            'Welcome aboard, Coach.',
  consumer:         "You're on the list.",
  facility:         "You're on the list.",
  general_waitlist: "You're on the list.",
};

const WAITLIST_LEADS = {
  investor:         'A member of our team will be in touch shortly.',
  coach:            "We're inviting coaches in waves. We'll reach out the moment your spot opens.",
  consumer:         "We're inviting new users in waves. We'll notify you the moment your spot opens.",
  facility:         "We're inviting new users in waves. We'll notify you the moment your spot opens.",
  general_waitlist: "We're inviting new users in waves. We'll notify you the moment your spot opens.",
};

export default function AuthModal({ onClose, initialMode = 'login' }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { isClosing, handleClose } = useModalTransition(onClose);

  const isPublic = PUBLIC_MODES.includes(initialMode);

  const [tab, setTab]       = useState(initialMode);
  const [role, setRole]     = useState('athlete');
  const [name, setName]     = useState('');
  const [email, setEmail]   = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  // beta tester result state
  const [betaResult, setBetaResult] = useState(null); // { status, message }

  // waitlist form fields
  const [wlName, setWlName]         = useState('');
  const [wlEmail, setWlEmail]       = useState('');
  const [wlCompany, setWlCompany]   = useState('');
  const [wlInterest, setWlInterest] = useState('general_waitlist');
  const [wlMessage, setWlMessage]   = useState('');
  const [wlSuccess, setWlSuccess]   = useState(null); // { headline, lead, emailOk, submittedEmail }

  function resetPublicState() {
    setBetaResult(null);
    setWlSuccess(null);
    setError('');
  }

  function switchTab(t) {
    setTab(t);
    resetPublicState();
  }

  // ── Auth submit ────────────────────────────────────────────────
  async function handleAuthSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (tab === 'login') {
        result = login(email, password, role);
      } else {
        if (!name.trim()) { setError('Name is required.'); setLoading(false); return; }
        result = register(name, email, password, role);
      }

      if (result.success) {
        handleClose();
        navigate('/app/dashboard');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Beta tester submit ─────────────────────────────────────────
  async function handleBetaSubmit(e) {
    e.preventDefault();
    setError('');
    setBetaResult(null);
    setLoading(true);

    try {
      const data = await checkBetaTester(email);

      if (!data.ok) {
        if (data.error === 'InvalidEmail') {
          setError('Please enter a valid email address.');
        } else {
          setError('Something went wrong. Please try again.');
        }
        return;
      }

      if (data.isBetaTester) {
        login(email, '', role);
        handleClose();
        navigate('/app/dashboard');
        return;
      }

      if (data.status === 'invited') {
        setBetaResult({ type: 'info', message: 'You have a pending invitation — check your email.' });
      } else if (data.status === 'expired') {
        setBetaResult({ type: 'warn', message: 'Your invitation has expired.', showWaitlist: true });
      } else {
        setBetaResult({ type: 'info', message: "You're not on the beta list yet.", showWaitlist: true });
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Waitlist submit ────────────────────────────────────────────
  async function handleWaitlistSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const payload = {
      name:         wlName.trim(),
      email:        wlEmail.trim(),
      company:      wlCompany.trim(),
      inquiry_type: toInquiryType(wlInterest),
      subject:      'Join Waitlist',
      message:      `Waitlist interest: ${wlInterest}\n\n${wlMessage.trim()}`,
      source_page:  'waitlist',
      source_url:   window.location.href,
    };

    try {
      const { res, data } = await submitWaitlist(payload);

      if (!res.ok || data.status !== 'ok') {
        setError(data.message || 'Submission failed. Please try again.');
        return;
      }

      const emailOk = !!(data.email && data.email.ok === true);
      setWlSuccess({
        headline:       WAITLIST_HEADLINES[wlInterest] || WAITLIST_HEADLINES.general_waitlist,
        lead:           WAITLIST_LEADS[wlInterest]     || WAITLIST_LEADS.general_waitlist,
        emailOk,
        submittedEmail: wlEmail.trim(),
      });
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Render helpers ─────────────────────────────────────────────

  const tabDefs = isPublic
    ? [{ key: 'betaTester', label: 'Beta Tester' }, { key: 'waitlist', label: 'Join Waitlist' }]
    : [{ key: 'login',      label: 'Sign In'      }, { key: 'register', label: 'Create Account' }];

  const ariaLabel = tab === 'betaTester' ? 'Beta tester access'
                  : tab === 'waitlist'   ? 'Join the fitndex waitlist'
                  : 'Sign in to fitndex';

  const panelKey = `${tab}${tab === 'waitlist' && wlSuccess ? '-success' : ''}`;
  const panelRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState(null);

  useLayoutEffect(() => {
    const node = panelRef.current;
    if (!node) return undefined;

    setBodyHeight(node.offsetHeight);

    const observer = new ResizeObserver(() => setBodyHeight(node.offsetHeight));
    observer.observe(node);
    return () => observer.disconnect();
  }, [panelKey]);

  return (
    <div
      className={`auth-modal-overlay${isClosing ? ' anim-fade-out' : ''}`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className={`auth-modal ${isClosing ? 'anim-fade-out-scale' : 'anim-fade-in-scale'}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        <div className="auth-modal__header">
          <span className="auth-modal__logo">fitndex</span>
          {onClose && (
            <button className="auth-modal__close" onClick={handleClose} aria-label="Close">
              <Icon name="close" size={16} />
            </button>
          )}
        </div>

        <div className="auth-modal__tabs" role="tablist">
          {tabDefs.map(({ key, label }) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              className={`auth-modal__tab ${tab === key ? 'auth-modal__tab--active' : ''}`}
              onClick={() => switchTab(key)}
              type="button"
            >
              {label}
            </button>
          ))}
        </div>

        {/* ── Auth forms ── */}
        <div className="auth-modal__body" style={{ height: bodyHeight != null ? `${bodyHeight}px` : 'auto' }}>
        <div className="auth-modal__panel" key={panelKey} ref={panelRef}>
        {!isPublic && (
          <form className="auth-modal__form" onSubmit={handleAuthSubmit} noValidate>
            {tab === 'register' && (
              <div className="auth-modal__field">
                <label className="auth-modal__label" htmlFor="auth-name">Full Name</label>
                <input
                  id="auth-name"
                  className="auth-modal__input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                  required
                />
              </div>
            )}

            <div className="auth-modal__field">
              <label className="auth-modal__label" htmlFor="auth-email">Email</label>
              <input
                id="auth-email"
                className="auth-modal__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="auth-modal__field">
              <label className="auth-modal__label" htmlFor="auth-password">Password</label>
              <input
                id="auth-password"
                className="auth-modal__input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
                required
              />
            </div>

            <div className="auth-modal__field">
              <label className="auth-modal__label">I am a</label>
              <div className="auth-modal__role-group" role="radiogroup" aria-label="Role">
                {['athlete', 'coach'].map((r) => (
                  <button
                    key={r}
                    type="button"
                    role="radio"
                    aria-checked={role === r}
                    className={`auth-modal__role-btn ${role === r ? 'auth-modal__role-btn--active' : ''}`}
                    onClick={() => setRole(r)}
                  >
                    <span className="auth-modal__role-icon">
                      <Icon name={r === 'athlete' ? 'athlete' : 'coach'} size={18} />
                    </span>
                    <span>{r.charAt(0).toUpperCase() + r.slice(1)}</span>
                  </button>
                ))}
              </div>
            </div>

            {error && <p className="auth-modal__error" role="alert">{error}</p>}

            <Button type="submit" variant="primary" size="lg" loading={loading} className="auth-modal__submit">
              {tab === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
        )}

        {/* ── Beta tester form ── */}
        {tab === 'betaTester' && (
          <form className="auth-modal__form" onSubmit={handleBetaSubmit} noValidate>
            <div className="auth-modal__field">
              <label className="auth-modal__label" htmlFor="beta-email">Beta tester email</label>
              <input
                id="beta-email"
                className="auth-modal__input"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setBetaResult(null); setError(''); }}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            {error && <p className="auth-modal__error" role="alert">{error}</p>}

            {betaResult && (
              <div className={`auth-modal__result auth-modal__result--${betaResult.type}`} role="status">
                <span>{betaResult.message}</span>
                {betaResult.showWaitlist && (
                  <button
                    type="button"
                    className="auth-modal__result-link"
                    onClick={() => switchTab('waitlist')}
                  >
                    Join the waitlist →
                  </button>
                )}
              </div>
            )}

            <Button type="submit" variant="primary" size="lg" loading={loading} className="auth-modal__submit">
              Check Access
            </Button>
          </form>
        )}

        {/* ── Waitlist form ── */}
        {tab === 'waitlist' && !wlSuccess && (
          <form className="auth-modal__form" onSubmit={handleWaitlistSubmit} noValidate>
            <div className="auth-modal__field">
              <label className="auth-modal__label" htmlFor="wl-name">Name</label>
              <input
                id="wl-name"
                className="auth-modal__input"
                type="text"
                value={wlName}
                onChange={(e) => setWlName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                required
              />
            </div>

            <div className="auth-modal__field">
              <label className="auth-modal__label" htmlFor="wl-email">Email</label>
              <input
                id="wl-email"
                className="auth-modal__input"
                type="email"
                value={wlEmail}
                onChange={(e) => setWlEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div className="auth-modal__field">
              <label className="auth-modal__label" htmlFor="wl-company">
                Company <span className="auth-modal__label-opt">(optional)</span>
              </label>
              <input
                id="wl-company"
                className="auth-modal__input"
                type="text"
                value={wlCompany}
                onChange={(e) => setWlCompany(e.target.value)}
                placeholder="Your organization"
                autoComplete="organization"
              />
            </div>

            <div className="auth-modal__field">
              <label className="auth-modal__label" htmlFor="wl-interest">Primary Interest</label>
              <select
                id="wl-interest"
                className="auth-modal__select"
                value={wlInterest}
                onChange={(e) => setWlInterest(e.target.value)}
              >
                {INTEREST_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="auth-modal__field">
              <label className="auth-modal__label" htmlFor="wl-message">Message</label>
              <textarea
                id="wl-message"
                className="auth-modal__textarea"
                value={wlMessage}
                onChange={(e) => setWlMessage(e.target.value)}
                placeholder="Tell us what you're most interested in."
                required
              />
            </div>

            {error && <p className="auth-modal__error" role="alert">{error}</p>}

            <Button type="submit" variant="primary" size="lg" loading={loading} className="auth-modal__submit">
              Join Waitlist
            </Button>
          </form>
        )}

        {/* ── Waitlist success state ── */}
        {tab === 'waitlist' && wlSuccess && (
          <div className="auth-modal__success" role="status" aria-live="polite">
            <div className="auth-modal__success-badge" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="5 12 10 17 19 8" />
              </svg>
            </div>
            <p className="auth-modal__success-headline">{wlSuccess.headline}</p>
            <p className="auth-modal__success-lead">{wlSuccess.lead}</p>
            {wlSuccess.emailOk && (
              <p className="auth-modal__success-detail">
                Confirmation sent to <strong>{wlSuccess.submittedEmail}</strong>.
              </p>
            )}
          </div>
        )}
        </div>
        </div>
      </div>
    </div>
  );
}
